/**
 * Copyright Mántum All Rights Reserved.
 */
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

import type { HttpError, Error } from '@mantum/kanela';

type Theme = {
    background: string;
    border: string;
    font: string;
};

const success: Theme = {
    background: 'text-lime-900',
    border: 'bg-lime-100',
    font: 'text-lime-400'
};

const error: Theme = {
    background: 'text-rose-900',
    border: 'bg-rose-100',
    font: 'text-rose-400'
};

const warning: Theme = {
    background: 'text-yellow-900',
    border: 'bg-yellow-100',
    font: 'text-yellow-400'
};

type Status = 'Success' | 'Error' | 'Warning';

type Internal = {
    status: Status;
    theme: Theme;
};

type PropTypes = {
    messages?: HttpError | string[] | Error;
    onClose?: () => void;
    status?: Status;
    title: string;
};

export default function Alert(props: PropTypes) {
    // prettier-ignore
    const {
        status = 'Success'
    } = props;

    let theme = success;

    if (status == 'Error') {
        theme = error;
    }

    if (status == 'Warning') {
        theme = warning;
    }

    let messages: string[] = [];
    if (props.messages) {
        if (Array.isArray(props.messages)) {
            messages = props.messages;
        } else {
            if ('errors' in props.messages) {
                const errors = props.messages.errors as Record<
                    string,
                    string[]
                >;

                messages = Object.keys(errors).flatMap((it) =>
                    errors[it].map((message) => `${message}`)
                );
            }
        }
    }

    return (
        <div className={`mb-4 w-full rounded p-4 ${theme.border} `}>
            <div className='flex flex-row justify-between'>
                <div className='flex flex-row gap-4 truncate'>
                    <Icon status={status} theme={theme} />
                    <p className={`truncate font-medium ${theme.background}`}>
                        {props.title}
                    </p>
                </div>
                {props.onClose && (
                    <button
                        className={theme.background}
                        data-testid='close'
                        onClick={props.onClose}
                        type='button'
                    >
                        <XMarkIcon className={`h-5 w-5 `} />
                    </button>
                )}
            </div>
            {messages.length > 0 && (
                <ul
                    className={`mt-1 list-inside list-disc pl-10 text-sm font-medium ${theme.background}`}
                >
                    {messages.map((it) => (
                        <li key={it}>{it}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const Icon = (props: Internal) => {
    let IconToDisplay = (
        <CheckCircleIcon className={`h-6 w-6 ${props.theme.font}`} />
    );

    if (props.status === 'Success') {
        IconToDisplay = (
            <CheckCircleIcon className={`h-6 w-6 ${props.theme.font}`} />
        );
    }

    if (props.status === 'Error') {
        IconToDisplay = (
            <XCircleIcon className={`h-6 w-6 ${props.theme.font}`} />
        );
    }

    if (props.status === 'Warning') {
        IconToDisplay = (
            <ExclamationCircleIcon className={`h-6 w-6 ${props.theme.font}`} />
        );
    }

    return IconToDisplay;
};
